/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin Order & Bank Transfer Approval (Task 11.3.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Standard Order Status Zod Schema
 */
export const OrderStatusSchema = z.enum(['pending', 'paid', 'failed', 'refunded']);
export type AdminOrderStatus = z.infer<typeof OrderStatusSchema>;

/**
 * Zod schema for single order item in the backoffice list.
 */
export const AdminOrderListItemSchema = z
  .object({
    id: z.string().openapi({ description: 'Unique order ID', example: 'ord_123456789' }),
    workspaceId: z.string().openapi({ description: 'Workspace UUID', example: 'ws_123456' }),
    workspaceName: z.string().openapi({ description: 'Workspace display name', example: 'Acme Studio' }),
    userId: z.string().nullable().optional().openapi({ description: 'User account ID who initiated order' }),
    userEmail: z.string().openapi({ description: 'Customer email address', example: 'customer@example.com' }),
    userName: z.string().openapi({ description: 'Customer full name', example: 'Nguyen Van A' }),
    planId: z.string().openapi({ description: 'Purchased Plan ID', example: 'plan_pro_monthly' }),
    planName: z.string().openapi({ description: 'Purchased Plan title', example: 'Pro Monthly Plan' }),
    planType: z.enum(['subscription', 'token_pack']).default('subscription').openapi({ description: 'Plan category' }),
    creditsIncluded: z.number().openapi({ description: 'Amount of AI credits granted upon payment', example: 50000 }),
    amountPaid: z.number().openapi({ description: 'Amount paid or wire expected', example: 29.0 }),
    currency: z.string().default('USD').openapi({ description: 'Currency ISO code', example: 'USD' }),
    gatewayCode: z.string().default('bank_transfer').openapi({ description: 'Payment gateway or method', example: 'bank_transfer' }),
    gatewayTransactionId: z.string().nullable().optional().openapi({ description: 'Bank wire reference code or transaction ID' }),
    status: OrderStatusSchema.openapi({ description: 'Order payment status', example: 'pending' }),
    proofFileR2: z.string().nullable().optional().openapi({ description: 'Relative R2 storage key of the proof image', example: 'receipts/ord_123.jpg' }),
    proofUrl: z.string().nullable().optional().openapi({ description: 'Resolved high-resolution media CDN or presigned view URL' }),
    rejectionReason: z.string().nullable().optional().openapi({ description: 'Explanation if the order was rejected' }),
    createdAt: z.string().openapi({ description: 'ISO 8601 creation timestamp' }),
  })
  .openapi('AdminOrderListItem');

export type AdminOrderListItem = z.infer<typeof AdminOrderListItemSchema>;

/**
 * Query schema for filtering orders.
 */
export const AdminOrderQuerySchema = z
  .object({
    status: z.enum(['all', 'pending', 'paid', 'failed', 'refunded']).default('all').openapi({ description: 'Filter by status' }),
    gatewayCode: z.string().optional().default('all').openapi({ description: 'Filter by gateway code' }),
    search: z.string().optional().openapi({ description: 'Search by Order ID, customer email, or wire code' }),
    page: z.coerce.number().int().min(1).default(1).openapi({ description: 'Page number' }),
    limit: z.coerce.number().int().min(1).max(100).default(20).openapi({ description: 'Entries per page' }),
  })
  .openapi('AdminOrderQuery');

export type AdminOrderQuery = z.infer<typeof AdminOrderQuerySchema>;

/**
 * Schema for approving an order.
 */
export const AdminApproveOrderSchema = z
  .object({
    note: z.string().optional().openapi({ description: 'Optional administrative note for ledger records' }),
  })
  .openapi('AdminApproveOrder');

export type AdminApproveOrderInput = z.infer<typeof AdminApproveOrderSchema>;

/**
 * Schema for rejecting an order.
 */
export const AdminRejectOrderSchema = z
  .object({
    reason: z
      .string()
      .min(3, { message: 'Rejection reason must be at least 3 characters long' })
      .openapi({
        description: 'Explanation for rejecting the wire transfer (emailed to user)',
        example: 'Receipt image is blurry and transaction code does not match bank statement.',
      }),
  })
  .openapi('AdminRejectOrder');

export type AdminRejectOrderInput = z.infer<typeof AdminRejectOrderSchema>;

/**
 * Response schema for approval operation.
 */
export const AdminOrderApprovalResponseSchema = z
  .object({
    orderId: z.string().openapi({ description: 'Approved order ID' }),
    status: OrderStatusSchema.openapi({ description: 'Resulting order status' }),
    creditsGranted: z.number().openapi({ description: 'Credits deposited to wallet' }),
    newWalletBalance: z.number().openapi({ description: 'Updated wallet balance' }),
    notifiedEmail: z.string().openapi({ description: 'Customer email receiving confirmation' }),
  })
  .openapi('AdminOrderApprovalResponse');

export type AdminOrderApprovalResponse = z.infer<typeof AdminOrderApprovalResponseSchema>;

/**
 * Summary KPI telemetry schema for bank transfer approval dashboard.
 */
export const AdminOrdersSummarySchema = z
  .object({
    pendingCount: z.number().openapi({ description: 'Total pending wire transfers awaiting approval' }),
    approvedCount: z.number().openapi({ description: 'Total approved orders' }),
    rejectedCount: z.number().openapi({ description: 'Total rejected orders' }),
    totalPendingAmountUsd: z.number().openapi({ description: 'Total pending monetary volume in USD' }),
    totalApprovedAmountUsd: z.number().openapi({ description: 'Total approved monetary volume in USD' }),
  })
  .openapi('AdminOrdersSummary');

export type AdminOrdersSummary = z.infer<typeof AdminOrdersSummarySchema>;
